import { ref } from 'vue'

import { CANVAS_HOOKS, IFRAME_ACTION_TYPES, IFRAME_DATA_MESSAGE } from '@lib/types/iframes'
import { type ComponentNode } from '@canvas-builder/types/canvas'
import { type CanvasRetrieve } from '@openapi'
import { type AppFile } from '@lib/types/app'
import { doesLocalOverrideExist, getLocalOverrideUrl } from '@lib/util/dev.util'
import { UI_MESSAGE_TYPES, type UiCanvasUpdatedPayload, type UiSectionListUpdatedPayload } from '@lib/sdk/main'

const hookIframesDict = ref<Record<string, Record<string, HTMLIFrameElement>>>({})

function onSectionListUpdateHook({
  sectionHookIframes,
  sectionListIds,
  sectionListComponentId,
  activeCanvas,
}: {
  sectionHookIframes?: Record<string, HTMLIFrameElement>
  sectionListIds: CanvasRetrieve['id'][]
  sectionListComponentId: ComponentNode['id']
  activeCanvas: CanvasRetrieve
}) {
  const iframes = sectionHookIframes || hookIframesDict.value[CANVAS_HOOKS.ON_SECTION_LIST_UPDATE] || {}

  const sectionListUpdatedPayload: UiSectionListUpdatedPayload = {
    section_list_ids: sectionListIds,
    section_list_component_id: sectionListComponentId,
    canvas: activeCanvas
      ? {
          ...activeCanvas,
          sections: (activeCanvas?.sections.map(({ content, ...s }) => s) ?? []) as CanvasRetrieve['sections'],
        } // remove unnecessary content from sections
      : null,
  }

  // TODO: Remove this once all embeddables are updated to use the new message type
  legacy___postHookMessage(iframes, IFRAME_ACTION_TYPES.SECTION_LIST_UPDATED, sectionListUpdatedPayload)
  postHookMessage(iframes, UI_MESSAGE_TYPES.UI_SECTION_LIST_UPDATED, sectionListUpdatedPayload)
}

function onCanvasUpdatedHook({
  hookIframes,
  activeCanvas,
}: {
  hookIframes?: Record<string, HTMLIFrameElement>
  activeCanvas: CanvasRetrieve
}) {
  const iframes = hookIframes || hookIframesDict.value[CANVAS_HOOKS.ON_CANVAS_UPDATE] || {}
  const canvasUpdatedPayload: UiCanvasUpdatedPayload = { canvas: activeCanvas }

  // TODO: Remove this once all embeddables are updated to use the new message type
  legacy___postHookMessage(iframes, IFRAME_ACTION_TYPES.CANVAS_UPDATED, canvasUpdatedPayload)
  postHookMessage(iframes, UI_MESSAGE_TYPES.UI_CANVAS_UPDATED, canvasUpdatedPayload)
}

function legacy___postHookMessage(iframes: Record<string, HTMLIFrameElement>, action: string, data: any) {
  for (const iframe of Object.values(iframes)) {
    if (!iframe?.contentWindow) {
      console.error('No iframe or contentWindow found. Cannot execute SECTION_LIST_UPDATED postMessage...')
      continue
    }

    iframe?.contentWindow?.postMessage(
      {
        type: IFRAME_DATA_MESSAGE,
        body: {
          action,
          data,
        },
      },
      '*',
    )
  }
}

function postHookMessage(iframes: Record<string, HTMLIFrameElement>, type: string, body: any) {
  for (const iframe of Object.values(iframes)) {
    if (!iframe?.contentWindow) {
      console.error('No iframe or contentWindow found. Cannot execute SECTION_LIST_UPDATED postMessage...')
      continue
    }

    iframe?.contentWindow?.postMessage(
      {
        type,
        body,
      },
      '*',
    )
  }
}

function getEntryForSectionHook(app: AppFile) {
  const embeddableSrc = getEmbeddableSrc(app)
  const onCanvasUpdateEntry = app.app_metadata?.hooks?.canvas?.on_canvas_update?.entry
  if (onCanvasUpdateEntry && onCanvasUpdateEntry !== 'index.html') {
    return embeddableSrc?.replace('index.html', onCanvasUpdateEntry)
  }
  return embeddableSrc
}
function getEmbeddableSrc(app: AppFile) {
  try {
    const appName = app.app_metadata?.name
    if (appName && doesLocalOverrideExist(appName)) {
      return getLocalOverrideUrl(appName)
    }
  } catch (e) {
    console.error('Error generating Embeddable debug url', e)
  }

  return app.content_url ?? undefined
}

function setHookRefs(iframeEl: HTMLIFrameElement | null, app: AppFile, hookEvent: string) {
  if (app && iframeEl) {
    hookIframesDict.value[hookEvent] = hookIframesDict.value[hookEvent] || {}
    hookIframesDict.value[hookEvent][app.id] = iframeEl
  }
}

function cleanup(app: AppFile, hookEvent: string) {
  const iframe = hookIframesDict.value[hookEvent]?.[app.id]
  if (iframe) {
    iframe.src = 'about:blank'
    iframe.onload = null
    delete hookIframesDict.value[hookEvent][app.id]
  }
}

export default function useCanvasHooks() {
  return {
    onSectionListUpdateHook,
    onCanvasUpdatedHook,
    getEmbeddableSrc,
    getEntryForSectionHook,
    setHookRefs,
    cleanup,
  }
}
